import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Sparkles, Calendar, Heart, Scroll, Flame, Library } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SacredSearchBar } from "./SacredSearchBar";
import { DEITIES } from "@/lib/sacred-library/deities";
import { FESTIVALS } from "@/lib/sacred-library/festivals";
import { VRATAS } from "@/lib/sacred-library/vratas";
import { STORIES } from "@/lib/sacred-library/stories";
import { SADHANAS } from "@/lib/sacred-library/sadhanas";
import { sacredPath, beginnerSummary, editPath } from "@/lib/sacred-library/utils/registry";
import { loadLinkRegistryFromApi, fetchPublishedOverrides } from "@/lib/sacred-library/sacred-library-api";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import { SacredIcon } from "./SacredIcon";
import { SacredCreateTopic } from "./SacredCreateTopic";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PURANA_TILES } from "@/lib/puranas";
import { getApiBaseUrl } from "@/lib/api-base";

const API = getApiBaseUrl();

const SECTIONS = [
  { type: "deity", label: "Deities", icon: Sparkles, items: DEITIES, path: "deities" },
  { type: "festival", label: "Festivals", icon: Calendar, items: FESTIVALS, path: "festivals" },
  { type: "vrata", label: "Vratas", icon: Heart, items: VRATAS, path: "vratas" },
  { type: "story", label: "Stories", icon: Scroll, items: STORIES, path: "stories" },
  { type: "sadhana", label: "Sādhanas", icon: Flame, items: SADHANAS, path: "sadhanas" },
];

function TopicCard({ type, entry, canEdit }) {
  const name = type === "story" ? entry.title : entry.name;
  return (
    <Card className="h-full hover:shadow-float transition-all bg-white/80">
      <CardContent className="p-4 flex items-center gap-2">
        <Link to={sacredPath(type, entry.id)} className="flex flex-1 items-center min-w-0" data-testid={`sacred-card-${type}-${entry.id}`}>
          <SacredIcon src={entry.image_url} alt={name} className="w-12 h-12 flex-shrink-0" />
          <div className="flex-1 ml-3 min-w-0">
            <h3 className="font-heading font-medium text-base mb-1">{name}</h3>
            <p className="text-xs text-muted-foreground line-clamp-2">{beginnerSummary(type, entry)}</p>
          </div>
        </Link>
        {canEdit && type !== "mantra" && (
          <Button size="sm" variant="outline" className="shrink-0" asChild>
            <Link to={editPath(type, entry.id)} title="Edit">
              <Pencil className="w-4 h-4" />
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function PuranaCard({ entry }) {
  return (
    <Card className="h-full hover:shadow-float transition-all bg-gradient-to-br from-primary/5 via-white to-secondary/5 border-primary/20">
      <CardContent className="p-4 flex items-center gap-2">
        <Link
          to={entry.path}
          className="flex flex-1 items-center min-w-0"
          data-testid={`purana-card-${entry.id}`}
        >
          <SacredIcon src={entry.image_url} alt={entry.title} className="w-12 h-12 flex-shrink-0" />
          <div className="flex-1 ml-3 min-w-0">
            <h3 className="font-heading font-medium text-base mb-1 text-primary">{entry.title}</h3>
            <p className="text-xs text-muted-foreground line-clamp-2">{entry.summary}</p>
          </div>
        </Link>
      </CardContent>
    </Card>
  );
}

export function SacredLibraryHub() {
  const { canEditLibrary } = useSacredAdmin();
  const [sections, setSections] = useState(SECTIONS);

  useEffect(() => {
    loadLinkRegistryFromApi();

    fetchPublishedOverrides().then((overrides) => {
      if (!overrides || overrides.length === 0) return;

      const updatedSections = SECTIONS.map((sec) => {
        const updatedItems = sec.items.map((item) => {
          const override = overrides.find((o) => o.type === sec.type && o.id === item.id);
          if (override) {
            const published = override.entry;
            const asset = override.asset_url;
            const assetVersion = override.asset_updated_at;
            const assetSrc = asset
              ? `${API}${asset}${assetVersion ? `?v=${encodeURIComponent(assetVersion)}` : ""}`
              : null;

            const img = assetSrc
              ? assetSrc
              : published?.image_url?.startsWith("/sacred-library/")
                ? `${API}${published.image_url}`
                : published?.image_url ?? item.image_url;

            return {
              ...item,
              ...(published || {}),
              image_url: img,
            };
          }
          return item;
        });
        return { ...sec, items: updatedItems };
      });

      setSections(updatedSections);
    });
  }, []);

  return (
    <div className="min-h-screen py-24 px-4 noise" data-testid="sacred-library-hub">
      <div className="max-w-6xl mx-auto">
        {canEditLibrary && <SacredCreateTopic />}
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl md:text-5xl font-medium mb-3">Sacred Library</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Unified knowledge for deities, festivals, vratas, stories, and sādhanas — beginner clarity with deep layers on demand.
          </p>
        </div>

        <SacredSearchBar autoFocus />

        <div className="mt-6 flex justify-center gap-3 flex-wrap">
          <Link
            to="/gitas"
            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            data-testid="library-gitas-link"
          >
            <BookOpen className="w-4 h-4" />
            Sacred Gītās (chapter summaries)
          </Link>
          <Link
            to="/library/scriptures"
            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            data-testid="library-scriptures-link"
          >
            <BookOpen className="w-4 h-4" />
            Scriptures, gurus & places (classic library)
          </Link>
        </div>

        <section className="mt-12">
          <h2 className="font-heading text-2xl mb-4 flex items-center gap-2">
            <Library className="w-5 h-5 text-primary" />
            Purāṇas
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PURANA_TILES.map((entry) => (
              <PuranaCard key={entry.id} entry={entry} />
            ))}
          </div>
        </section>

        {sections.map(({ type, label, icon: Icon, items }) => (
          <section key={type} className="mt-12">
            <h2 className="font-heading text-2xl mb-4 flex items-center gap-2">
              <Icon className="w-5 h-5 text-primary" />
              {label}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((entry) => (
                <TopicCard key={entry.id} type={type} entry={entry} canEdit={canEditLibrary} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
